import { collection, getDocs, doc, getDoc, query, where, limit } from 'firebase/firestore';
import { db } from './firebase';
import { USER_TYPES } from '../constants/designer';
import { SHOP_SOCIAL_IDS } from '../constants/shop';
import { searchKey } from '../utils/searchKey';

// ── SHOP DIRECTORY ───────────────────────────────────────────────────────────
// The exact counterpart of designerService, and deliberately so: a shop is
// another kind of account on users/{uid}, told apart by `userType`, with its
// public fields nested under `shop`. Everything the designer directory learned
// applies unchanged here —
//
//   · NO MIRROR COLLECTION. One document is the source of truth, so a profile
//     edit shows up in search immediately and the two can never drift.
//   · SEARCH IS CLIENT-SIDE. Firestore matches a prefix, never a substring, so
//     a server query would find "Stone" from "Sto" but never from "tone", and
//     every filter combination would want its own composite index. Fetching the
//     directory once and filtering in memory gives substring matching on any
//     field, instant results with no per-keystroke reads, and no index to keep.
//
// The trade is the same too: the whole directory loads up front, which is fine
// into the low thousands of shops, and this module is the single place to swap
// in a search service when it isn't.
//
// WHY NOT ONE GENERIC DIRECTORY FOR BOTH ROLES. It was tempting — the caching
// is identical. But the cards are not: a designer is filtered by styles,
// experience and rate, a shop by categories and delivery, and the two searches
// are separate screens that must be able to hold their own caches at once. A
// shared module would have spent its every function branching on which role it
// was being called for, which is a worse cost than this file's duplication.
const MAX_SHOPS = 1000;
const CACHE_TTL_MS = 5 * 60 * 1000;

// users/{uid} → the flat card the UI works with. The profile fields live nested
// under `shop`; flattening here means the search page, the profile page and the
// recommendation rail never have to know that.
function toCard(id, u) {
  const s = u.shop || {};
  return {
    id,
    uid: id,
    // `fullName` is the shop's name at the top level — written by
    // updateShopProfile so that every role-agnostic consumer (comment bylines,
    // OwnerBadge, chat headers) keeps working without knowing what a shop is.
    // `s.name` is the fallback for a document written before that.
    name: u.fullName || s.name || '',
    // The logo IS the avatar; see the note in userService.createUserDocument.
    avatarUrl: u.avatarUrl || '',
    // Maintained by the like trigger, never written from the client.
    likeCount: u.likeCount || 0,
    // ── THE TIER, because the directory is where placement is decided ────────
    // Search ordering, the recommendation rail, the homepage row and the tint
    // on a card all read this, and every one of them resolves a missing value
    // to free — so leaving it out of this whitelist does not fail loudly, it
    // quietly makes every shop look and rank like a free one. That is exactly
    // what happened to the designer card before it listed `plan`.
    //
    // `credits` is deliberately NOT here: nothing public renders a balance, and
    // this list is served to every visitor.
    plan: u.plan || 'free',
    categories: s.categories || [],
    delivery: !!s.delivery,
    location: s.location || '',
    // The full picked line — the address a customer needs in order to turn up.
    // The `location` above stays the coarse key search groups on; see
    // LocationPicker. Falls back to it for shops saved before this existed.
    locationAddress: s.locationAddress || s.location || '',
    locationCoords: s.locationCoords || null,
    description: s.description || '',
    website: s.website || '',
    socials: s.socials || {},
  };
}

let _cache = null;
let _cachedAt = 0;
let _inflight = null;

/**
 * Applies a like/unlike to the CACHED copy of a shop, so a count you just
 * changed doesn't reappear stale — like a shop, press Back, and the feed would
 * otherwise render from a cache filled up to five minutes ago and show the old
 * number, making the like look like it never happened.
 *
 * Dropping the whole cache instead would re-read every shop for a change of one
 * integer on one row. The trigger remains the source of truth
 * (functions/onLikeWrite.js); this only keeps the local copy honest.
 */
export function patchShopLikeCount(uid, delta) {
  if (!uid || !delta) return;
  // ONCE PER OBJECT, not once per cache — see patchDesignerLikeCount, which
  // had the same defect for the same reason. The per-uid cache holds its own
  // copy when the shop was fetched directly, but the SAME object when the
  // directory already had it, so patching both by name moved the count by two.
  const seen = new Set();
  const bump = (row) => {
    if (!row || seen.has(row)) return;
    seen.add(row);
    row.likeCount = Math.max(0, (row.likeCount || 0) + delta);
  };
  bump(_cache?.find(s => (s.uid || s.id) === uid));
  bump(_one.get(uid)?.card);
}

/**
 * Forgets a shop, so the next read is fresh.
 *
 * MUST be called whenever a shop's own document changes. Without it, a shop
 * that edits its profile keeps seeing the pre-edit version for the rest of the
 * cache TTL — the one case where caching is a visible regression rather than a
 * saving.
 */
export function invalidateShop(uid) {
  if (uid) _one.delete(uid);
  // The directory row is stale for the same reason.
  _cache = null;
  _cachedAt = 0;
}

/** Every shop account. One query, filtered on userType. */
export async function fetchShops({ force = false } = {}) {
  const fresh = _cache && Date.now() - _cachedAt < CACHE_TTL_MS;
  if (fresh && !force) return _cache;
  if (_inflight && !force) return _inflight;

  _inflight = (async () => {
    try {
      const snap = await getDocs(query(
        collection(db, 'users'),
        where('userType', '==', USER_TYPES.SHOP),
        limit(MAX_SHOPS),
      ));
      _cache = snap.docs
        .map(d => toCard(d.id, d.data()))
        // A profile with no name was never filled in; it would only ever show
        // as a blank row.
        .filter(s => s.name.trim());
      _cachedAt = Date.now();
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

// ── ONE SHOP ─────────────────────────────────────────────────────────────────
// Memoised per uid, including the NEGATIVE result. Every OwnerBadge on a page
// asks for its author, so an ungated version costs one document read per tile.
// Caching `null` matters as much as caching a hit — a customer's document is
// unreadable by the rules, and without this every render retries a request that
// is guaranteed to be refused.
const _one = new Map();         // uid -> { card, at }
const _oneInflight = new Map(); // uid -> Promise, so N badges mounting at once
                                // share ONE request instead of racing.

/** One shop, or null if that uid isn't a (readable) shop. */
export async function fetchShop(uid, { force = false } = {}) {
  if (!uid) return null;

  if (!force) {
    const hit = _one.get(uid);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.card;
    if (_oneInflight.has(uid)) return _oneInflight.get(uid);

    // The directory may already hold this shop — the feed and search fill it —
    // in which case there is nothing to fetch at all.
    const fromList = _cache?.find(s => (s.uid || s.id) === uid);
    if (fromList) {
      _one.set(uid, { card: fromList, at: Date.now() });
      return fromList;
    }
  }

  const req = (async () => {
    // A CUSTOMER's document is private, so reading one is denied by the rules —
    // correct behaviour, not a failure, and it must surface as "no profile
    // here" rather than an uncaught FirebaseError.
    let snap;
    try {
      snap = await getDoc(doc(db, 'users', uid));
    } catch {
      return null;
    }
    if (!snap.exists()) return null;
    const u = snap.data();
    if (u.userType !== USER_TYPES.SHOP) return null;
    return toCard(snap.id, u);
  })();

  _oneInflight.set(uid, req);
  try {
    const card = await req;
    _one.set(uid, { card, at: Date.now() });
    return card;
  } finally {
    _oneInflight.delete(uid);
  }
}

// Normalises for comparison: lowercase, accents stripped, so "José" typed as
// "Jose" still matches.
// Lowercased, accents stripped, and TRANSLITERATED — so `ator` finds
// `աթոռ` and `стол` finds `stol`. Was a private copy of a
// lowercase-and-strip helper that each of the three directories kept its
// own identical version of; see utils/searchKey for what it does now and
// what it deliberately does not.
const norm = searchKey;

/**
 * Filters the directory.
 *
 * @param shops     rows from fetchShops()
 * @param q         free text — matches name, category labels, location, bio
 * @param filters   { categories: string[], location: string, delivery: boolean }
 * @param catLabel  (key) => translated label, so a search for "furniture" in
 *                  any of the three locales still hits the right shop
 */
export function filterShops(shops, q, filters = {}, catLabel = (k) => k) {
  const needle = norm(q);
  const { categories = [], location = '', delivery = false } = filters;
  const loc = norm(location);

  return shops.filter(s => {
    // ── free text ──
    if (needle) {
      const haystack = [
        s.name,
        s.location,
        s.description,
        // Category KEYS and their translated labels, so "мебель" and
        // "furniture" both find the same shop.
        ...(s.categories || []),
        ...(s.categories || []).map(catLabel),
      ].map(norm).join(' ');
      if (!haystack.includes(needle)) return false;
    }

    // ── categories: ALL selected must be present ──
    // "Furniture AND Lighting" is a narrowing filter; ANY-matching would make
    // each extra selection return MORE results, the opposite of what ticking
    // another box implies.
    if (categories.length && !categories.every(c => (s.categories || []).includes(c))) return false;

    // ── delivery ──
    // Only ever narrows. Unticked means "don't care", not "must not deliver" —
    // a tri-state here would be a control nobody asked for.
    if (delivery && !s.delivery) return false;

    // ── location ──
    if (loc && !norm(s.location).includes(loc)) return false;

    return true;
  });
}

/** Distinct locations present in the directory, for the filter dropdown. */
export function locationsOf(shops) {
  const seen = new Map();
  for (const s of shops) {
    const l = (s.location || '').trim();
    if (l && !seen.has(norm(l))) seen.set(norm(l), l);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/** The channels a shop actually filled in, in the fixed display order. */
export function socialsOf(shop) {
  return SHOP_SOCIAL_IDS
    .filter(id => String(shop?.socials?.[id] || '').trim())
    .map(id => ({ id, value: shop.socials[id] }));
}
